import React, { useCallback, useEffect, useRef, useState } from 'react'
import CableIcon from '@mui/icons-material/Cable'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import LockIcon from '@mui/icons-material/Lock'
import LogoutIcon from '@mui/icons-material/Logout'
import SaveIcon from '@mui/icons-material/Save'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WifiIcon from '@mui/icons-material/Wifi'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Link, useNavigate } from 'react-router'
import { PrimaryButton, SecondaryButton } from '../components/Buttons'
import useSerialConnection from '../hooks/useSerialConnection'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import {
  clearLoginSession,
  clearPendingDeviceAssignment,
  getInverterModes,
  getLoginSession,
  getMeterModels,
  getPendingDeviceAssignment,
  saveBoardConfig,
  updateUserDeviceIds,
} from '../services/authApi'
import {
  RS485_BAUD_OPTIONS,
  RS485_DATA_BIT_OPTIONS,
  RS485_FALLBACK_MODEL_OPTIONS,
  RS485_MODE_OPTIONS,
  RS485_PARITY_OPTIONS,
  RS485_STOP_BIT_OPTIONS,
  buildRs485Com1Config,
  ensureSelectedOption,
  getOptionLabel,
  getOptionValue,
} from '../services/rs485Settings'
import './TechConfigPage.css'

const DEFAULT_MAP_POSITION = [15.1456, 120.5896]
const SERIAL_RESPONSE_TIMEOUT_MS = 5000
const SERIAL_RESPONSE_WARNING = 'Serial connected, but the device did not send configuration values. Check the board and reconnect.'

function toStringValue(value, fallback = '') {
  return value === undefined || value === null ? fallback : String(value)
}

function toIndexedModelOptions(models) {
  return models.map((model, index) => ({
    label: String(model),
    value: String(index),
  }))
}

function getSessionCompany() {
  const pendingAssignment = getPendingDeviceAssignment()

  if (pendingAssignment?.userId) {
    return toStringValue(pendingAssignment.userId)
  }

  const user = getLoginSession()?.user || {}

  return toStringValue(user.userId ?? user.id)
}

function createDefaultFormValues() {
  return {
    deviceId: '',
    company: getSessionCompany(),
    location: '',
    latitude: '15.1456',
    longitude: '120.5896',
    ssid: '',
    wifiPassword: '',
    mqttServer: '',
    mqttPort: '8883',
    mqttUser: '',
    mqttPassword: '',
    com1Enabled: false,
    com1Address: '',
    com1Address2: '',
    com1Mode: '0',
    com1Model: '',
    com1Model2: '',
    com1Baud: '',
    com1DataBit: '',
    com1StopBit: '',
    com1Parity: '',
  }
}

function unwrapConfiguration(payload) {
  if (payload?.config?.Device) {
    return payload.config
  }

  if (payload?.data?.Device) {
    return payload.data
  }

  return payload || {}
}

function configurationToFormValues(configuration, currentValues) {
  const device = configuration.Device || {}
  const wifi = configuration.WiFi || {}
  const mqtt = configuration.MQTT || {}
  const com1 = configuration.RS485?.COM1 || {}

  return {
    ...currentValues,
    deviceId: toStringValue(device.ID),
    company: toStringValue(device.Company, currentValues.company),
    location: toStringValue(device.Location),
    latitude: toStringValue(device.Lat, '15.1456'),
    longitude: toStringValue(device.Lon, '120.5896'),
    ssid: toStringValue(wifi.ssid_0),
    wifiPassword: toStringValue(wifi.pass_0),
    mqttServer: toStringValue(mqtt.Server),
    mqttPort: toStringValue(mqtt.Port, '8883'),
    mqttUser: toStringValue(mqtt.User),
    mqttPassword: toStringValue(mqtt.Pass),
    com1Enabled: com1.en === true || toStringValue(com1.en) === '1',
    com1Address: toStringValue(com1.addr),
    com1Address2: toStringValue(com1.addr2),
    com1Mode: toStringValue(com1.mode, '0'),
    com1Model: toStringValue(com1.model),
    com1Model2: toStringValue(com1.model2),
    com1Baud: toStringValue(com1.baud),
    com1DataBit: toStringValue(com1.dbit),
    com1StopBit: toStringValue(com1.sbit),
    com1Parity: toStringValue(com1.parity),
  }
}

function buildConfiguration(formValues, previousConfiguration) {
  const previousWifi = previousConfiguration.WiFi || {}
  const previousRs485 = previousConfiguration.RS485 || {}

  return {
    ...previousConfiguration,
    cmd: 'con',
    fw: previousConfiguration.fw || '',
    Date: new Date().toISOString(),
    Device: {
      ...previousConfiguration.Device,
      ID: formValues.deviceId.trim(),
      Company: formValues.company.trim(),
      Location: formValues.location.trim(),
      Lat: String(formValues.latitude),
      Lon: String(formValues.longitude),
    },
    MQTT: {
      ...previousConfiguration.MQTT,
      Server: formValues.mqttServer,
      Port: formValues.mqttPort,
      User: formValues.mqttUser,
      Pass: formValues.mqttPassword,
    },
    WiFi: {
      ...previousWifi,
      ssid_0: formValues.ssid,
      pass_0: formValues.wifiPassword,
      ssid_1: previousWifi.ssid_1 || '',
      ssid_2: previousWifi.ssid_2 || '',
      ssid_3: previousWifi.ssid_3 || '',
      ssid_4: previousWifi.ssid_4 || '',
      pass_1: previousWifi.pass_1 || '',
      pass_2: previousWifi.pass_2 || '',
      pass_3: previousWifi.pass_3 || '',
      pass_4: previousWifi.pass_4 || '',
    },
    RS485: {
      ...previousRs485,
      COM1: buildRs485Com1Config(previousRs485.COM1, formValues),
      COM2: previousRs485.COM2 || {
        en: '0',
        addr: '',
        mode: 0,
        model: '',
        baud: '',
        dbit: '',
        sbit: '',
        parity: '',
      },
    },
  }
}

function createTechConfigIcon() {
  return L.divIcon({
    className: 'tech-config__map-marker',
    html: '<span></span>',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  })
}

function toCoordinate(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : null
}

function Field({ label, children }) {
  return (
    <label className="tech-config__field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function TextInput({ label, ...props }) {
  return (
    <Field label={label}>
      <input {...props} />
    </Field>
  )
}

function SelectInput({ label, children, ...props }) {
  return (
    <Field label={label}>
      <select {...props}>{children}</select>
    </Field>
  )
}

function TechConfigPage({
  isDarkMode = false,
  onToggleTheme,
  embedded = false,
  onDeviceAssigned,
}) {
  const navigate = useNavigate()
  const [pendingAssignment, setPendingAssignment] = useState(getPendingDeviceAssignment)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSerialWarningOpen, setIsSerialWarningOpen] = useState(false)
  const [showWifiPassword, setShowWifiPassword] = useState(false)
  const [modelOptions, setModelOptions] = useState(() => toIndexedModelOptions(RS485_FALLBACK_MODEL_OPTIONS))
  const [deviceConfiguration, setDeviceConfiguration] = useState(null)
  const [statusMessage, setStatusMessage] = useState({
    type: 'info',
    text: 'Connect a serial device to load its configuration.',
  })
  const mapElementRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerRef = useRef(null)
  const isEditingRef = useRef(isEditing)
  const assignedDeviceIdRef = useRef('')
  const serialResponseTimerRef = useRef(null)
  const awaitingSerialResponseRef = useRef(false)
  const [formValues, setFormValues] = useState(createDefaultFormValues)

  const clearSerialResponseTimer = useCallback(() => {
    if (serialResponseTimerRef.current) {
      window.clearTimeout(serialResponseTimerRef.current)
      serialResponseTimerRef.current = null
    }
  }, [])

  const handleSerialData = useCallback((payload) => {
    const configuration = unwrapConfiguration(payload)

    if (!configuration.Device) {
      return
    }

    awaitingSerialResponseRef.current = false
    clearSerialResponseTimer()
    setIsSerialWarningOpen(false)
    setDeviceConfiguration(configuration)
    setFormValues((currentValues) => configurationToFormValues(configuration, currentValues))
    setIsEditing(false)
    setStatusMessage({
      type: 'success',
      text: `Configuration loaded from ${configuration.Device.ID || 'the connected device'}.`,
    })
  }, [clearSerialResponseTimer])

  const {
    connect,
    disconnect,
    sendAndReconnect,
    portState,
    isBusy,
    isSupported,
  } = useSerialConnection({ onData: handleSerialData })
  const isConnected = portState === 'open'

  const isReadOnly = !isEditing
  const isCom1Inverter = formValues.com1Mode === '1'

  useEffect(() => {
    isEditingRef.current = isEditing
  }, [isEditing])

  useEffect(() => clearSerialResponseTimer, [clearSerialResponseTimer])

  useEffect(() => {
    if (!isSupported) {
      setStatusMessage({
        type: 'error',
        text: 'Web Serial is unavailable. Open this page in desktop Chrome or Edge over HTTPS.',
      })
    }
  }, [isSupported])

  useEffect(() => {
    const deviceId = toStringValue(deviceConfiguration?.Device?.ID).trim()

    if (
      !pendingAssignment?.technicianAuthorized
      || !deviceId
      || assignedDeviceIdRef.current === deviceId
    ) {
      return
    }

    let isActive = true
    const nextDeviceIds = Array.from(new Set([
      ...(pendingAssignment.deviceIds || []),
      deviceId,
    ].map((value) => String(value).trim()).filter(Boolean)))

    assignedDeviceIdRef.current = deviceId
    setFormValues((currentValues) => ({
      ...currentValues,
      company: toStringValue(pendingAssignment.userId),
    }))
    setStatusMessage({
      type: 'info',
      text: `Assigning ${deviceId} to ${pendingAssignment.displayName || pendingAssignment.email || 'the customer'}...`,
    })

    updateUserDeviceIds(pendingAssignment.userId, nextDeviceIds)
      .then(() => {
        if (!isActive) {
          return
        }

        clearPendingDeviceAssignment()
        setPendingAssignment(null)
        onDeviceAssigned?.(deviceId)
        setStatusMessage({
          type: 'success',
          text: `${deviceId} was assigned to ${pendingAssignment.displayName || pendingAssignment.email || 'the customer'}.`,
        })
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        assignedDeviceIdRef.current = ''
        setStatusMessage({
          type: 'error',
          text: error.message || 'Unable to assign the device to the customer.',
        })
      })

    return () => {
      isActive = false
    }
  }, [deviceConfiguration, onDeviceAssigned, pendingAssignment])

  useEffect(() => {
    const isInverter = formValues.com1Mode === '1'
    const loadModels = isInverter ? getInverterModes : getMeterModels
    let isActive = true

    setModelOptions(isInverter ? [] : toIndexedModelOptions(RS485_FALLBACK_MODEL_OPTIONS))

    loadModels()
      .then((nextModels) => {
        if (isActive) {
          setModelOptions(toIndexedModelOptions(nextModels))
        }
      })
      .catch((error) => {
        console.error(`Error fetching ${isInverter ? 'inverter' : 'meter'} models:`, error)
      })

    return () => {
      isActive = false
    }
  }, [formValues.com1Mode])

  useEffect(() => {
    if (!mapElementRef.current || mapInstanceRef.current) {
      return undefined
    }

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(DEFAULT_MAP_POSITION, 17)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    const marker = L.marker(DEFAULT_MAP_POSITION, { icon: createTechConfigIcon() }).addTo(map)

    map.on('click', (event) => {
      if (!isEditingRef.current) {
        return
      }

      const nextLatitude = event.latlng.lat.toFixed(6)
      const nextLongitude = event.latlng.lng.toFixed(6)

      marker.setLatLng(event.latlng)
      setFormValues((currentValues) => ({
        ...currentValues,
        latitude: nextLatitude,
        longitude: nextLongitude,
      }))
    })

    mapInstanceRef.current = map
    markerRef.current = marker
    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.remove()
      mapInstanceRef.current = null
      markerRef.current = null
    }
  }, [])

  useEffect(() => {
    const latitude = toCoordinate(formValues.latitude)
    const longitude = toCoordinate(formValues.longitude)

    if (latitude === null || longitude === null || !mapInstanceRef.current || !markerRef.current) {
      return
    }

    const nextPosition = [latitude, longitude]

    markerRef.current.setLatLng(nextPosition)
    mapInstanceRef.current.setView(nextPosition, Math.max(mapInstanceRef.current.getZoom(), 17), {
      animate: true,
    })
  }, [formValues.latitude, formValues.longitude])

  const updateValue = (event) => {
    const { checked, name, type, value } = event.target

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'com1Mode' && value === '1' ? { com1Model: '', com1Address2: '', com1Model2: '' } : {}),
      ...(name === 'com1Mode' && value !== '1' ? { com1Model: '', com1Model2: '' } : {}),
    }))
  }

  const handleConnect = async () => {
    setIsEditing(false)

    if (isConnected) {
      awaitingSerialResponseRef.current = false
      clearSerialResponseTimer()
      setIsSerialWarningOpen(false)
      await disconnect()
      setDeviceConfiguration(null)
      setFormValues(createDefaultFormValues())
      setStatusMessage({
        type: 'info',
        text: 'Serial device disconnected.',
      })
      return
    }

    setStatusMessage({
      type: 'info',
      text: 'Choose the board from the browser serial-port prompt.',
    })
    awaitingSerialResponseRef.current = true
    clearSerialResponseTimer()
    setIsSerialWarningOpen(false)

    try {
      await connect()

      if (awaitingSerialResponseRef.current) {
        serialResponseTimerRef.current = window.setTimeout(() => {
          if (!awaitingSerialResponseRef.current) {
            return
          }

          console.warn('[Serial RX]', SERIAL_RESPONSE_WARNING)
          setIsSerialWarningOpen(true)
          serialResponseTimerRef.current = null
        }, SERIAL_RESPONSE_TIMEOUT_MS)
      }
    } catch (error) {
      awaitingSerialResponseRef.current = false
      clearSerialResponseTimer()
      setIsSerialWarningOpen(false)
      setStatusMessage({
        type: 'error',
        text: error.name === 'NotFoundError'
          ? 'No serial device was selected.'
          : error.message || 'Unable to connect to the serial device.',
      })
    }
  }

  const handleEditSave = async (event) => {
    event.preventDefault()

    if (!isEditing) {
      if (!isConnected) {
        setStatusMessage({
          type: 'error',
          text: 'Connect a serial device before editing its configuration.',
        })
        return
      }

      setIsEditing(true)
      return
    }

    const deviceId = formValues.deviceId.trim()

    if (!deviceId) {
      setStatusMessage({
        type: 'error',
        text: 'Device ID is required before the configuration can be saved.',
      })
      return
    }

    const configuration = buildConfiguration(formValues, deviceConfiguration || {})

    setIsSaving(true)
    setStatusMessage({
      type: 'info',
      text: 'Saving configuration to the server...',
    })

    let serverWasUpdated = false

    try {
      await saveBoardConfig(deviceId, configuration)
      serverWasUpdated = true
      setStatusMessage({
        type: 'info',
        text: 'Server updated. Sending configuration to the serial device...',
      })
      console.log('[Board Config Serial Send]', configuration)
      await sendAndReconnect(configuration)
      setDeviceConfiguration(configuration)
      setIsEditing(false)
      setStatusMessage({
        type: 'success',
        text: 'Configuration saved to the server and serial device.',
      })
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: serverWasUpdated
          ? `The server was updated, but the serial device failed: ${error.message || 'Unknown serial error.'}`
          : error.message || 'Unable to save the device configuration.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (deviceConfiguration) {
      setFormValues((currentValues) => configurationToFormValues(deviceConfiguration, currentValues))
    }

    setIsEditing(false)
    setStatusMessage({
      type: 'info',
      text: 'Unsaved changes were discarded.',
    })
  }

  const handleLogout = async () => {
    if (isConnected) {
      await disconnect()
    }

    clearPendingDeviceAssignment()
    clearLoginSession()
    navigate('/login', { replace: true })
  }

  return (
    <main className={`tech-config ${embedded ? 'tech-config--embedded' : ''}`}>
      {!embedded ? (
        <header className="tech-config__topbar">
          <Link className="tech-config__brand" to="/" aria-label="CNERGI home">
            <img src={cnergiLogo} alt="CNERGI" />
            <span>Technician Configuration</span>
          </Link>

          <div className="tech-config__topbar-actions">
            <button
              className="tech-config__theme-toggle"
              type="button"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={onToggleTheme}
            >
              {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
            <button
              className="tech-config__logout"
              type="button"
              onClick={handleLogout}
              disabled={isBusy || isSaving}
            >
              <LogoutIcon aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </header>
      ) : null}

      <section className="tech-config__hero">
        <div>
          <p>Board setup</p>
          <h1>Configure device identity, network access, and RS485 settings.</h1>
        </div>
        <div className={`tech-config__status ${isConnected ? 'tech-config__status--connected' : ''}`}>
          <CableIcon aria-hidden="true" />
          <span>{isConnected ? 'Serial connected' : 'Serial disconnected'}</span>
        </div>
      </section>

      <form className="tech-config__workspace" onSubmit={handleEditSave}>
        <div className="tech-config__toolbar">
          <SecondaryButton type="button" onClick={handleConnect} disabled={isBusy || isSaving || !isSupported}>
            <CableIcon aria-hidden="true" />
            {isConnected ? 'Disconnect Device' : isBusy ? 'Connecting...' : 'Connect Device'}
          </SecondaryButton>
          <div className="tech-config__toolbar-actions">
            {isEditing ? (
              <SecondaryButton type="button" onClick={handleCancel} disabled={isBusy || isSaving}>
                Cancel
              </SecondaryButton>
            ) : null}
            <PrimaryButton type="submit" disabled={isSaving || !isConnected}>
              {isEditing ? <SaveIcon aria-hidden="true" /> : <TuneIcon aria-hidden="true" />}
              {isSaving ? 'Saving...' : isEditing ? 'Save Config' : 'Edit Config'}
            </PrimaryButton>
          </div>
        </div>

        <p
          className={`tech-config__message tech-config__message--${statusMessage.type}`}
          role={statusMessage.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {statusMessage.text}
        </p>

        <div className="tech-config__grid">
          <section className="tech-config__section tech-config__section--device">
            <div className="tech-config__section-heading">
              <SettingsInputComponentIcon aria-hidden="true" />
              <div>
                <p>Device</p>
                <h2>Device Info</h2>
              </div>
            </div>

            <div className="tech-config__fields">
              <TextInput label="Device ID" name="deviceId" value={formValues.deviceId} onChange={updateValue} disabled placeholder="CNERGI-001" />
              <TextInput label="Company" name="company" value={formValues.company} onChange={updateValue} disabled placeholder="Company name" />
              <TextInput label="Location" name="location" value={formValues.location} onChange={updateValue} disabled={isReadOnly} placeholder="Building or site" />
              <div className="tech-config__field-pair">
                <TextInput label="Latitude" name="latitude" value={formValues.latitude} onChange={updateValue} disabled={isReadOnly} type="number" step="any" />
                <TextInput label="Longitude" name="longitude" value={formValues.longitude} onChange={updateValue} disabled={isReadOnly} type="number" step="any" />
              </div>
            </div>

            <div className="tech-config__map" aria-label="Device location map">
              <div className="tech-config__map-canvas" ref={mapElementRef} aria-label="OpenStreetMap device location"></div>
              <div className="tech-config__map-coordinates">
                <div>
                  <strong>{formValues.latitude}</strong>
                  <span>Latitude</span>
                </div>
                <div>
                  <strong>{formValues.longitude}</strong>
                  <span>Longitude</span>
                </div>
              </div>
            </div>
          </section>

          <div className="tech-config__stack">
            <section className="tech-config__section">
              <div className="tech-config__section-heading">
                <WifiIcon aria-hidden="true" />
                <div>
                  <p>Network</p>
                  <h2>WiFi Setting</h2>
                </div>
              </div>

              <div className="tech-config__fields">
                <TextInput label="SSID" name="ssid" value={formValues.ssid} onChange={updateValue} disabled={isReadOnly} placeholder="Network name" />
                <Field label="Password">
                  <div className="tech-config__password-wrap">
                    <input
                      type={showWifiPassword ? 'text' : 'password'}
                      name="wifiPassword"
                      value={formValues.wifiPassword}
                      onChange={updateValue}
                      disabled={isReadOnly}
                      placeholder="WiFi password"
                    />
                    <button
                      type="button"
                      aria-label={showWifiPassword ? 'Hide WiFi password' : 'Show WiFi password'}
                      onClick={() => setShowWifiPassword((current) => !current)}
                    >
                      {showWifiPassword ? <VisibilityOffIcon aria-hidden="true" /> : <VisibilityIcon aria-hidden="true" />}
                    </button>
                  </div>
                </Field>
              </div>
            </section>

            {/*
            <section className="tech-config__section">
              <div className="tech-config__section-heading">
                <RouterIcon aria-hidden="true" />
                <div>
                  <p>Messaging</p>
                  <h2>MQTT</h2>
                </div>
              </div>

              <div className="tech-config__fields">
                <TextInput label="Server" name="mqttServer" value={formValues.mqttServer} onChange={updateValue} disabled={isReadOnly} placeholder="mqtt.example.com" />
                <div className="tech-config__field-pair">
                  <TextInput label="Port" name="mqttPort" value={formValues.mqttPort} onChange={updateValue} disabled={isReadOnly} inputMode="numeric" />
                  <TextInput label="User" name="mqttUser" value={formValues.mqttUser} onChange={updateValue} disabled={isReadOnly} />
                </div>
                <TextInput label="Password" name="mqttPassword" type="password" value={formValues.mqttPassword} onChange={updateValue} disabled={isReadOnly} />
              </div>
            </section>
            */}
          </div>

          <section className="tech-config__section tech-config__section--rs485">
            <div className="tech-config__section-heading">
              <SettingsInputComponentIcon aria-hidden="true" />
              <div>
                <p>Serial bus</p>
                <h2>RS485</h2>
              </div>
            </div>

            <div className="tech-config__com-panel">
              <div className="tech-config__com-header">
                <div>
                  <strong>COM 1</strong>
                  <span>Meter communication profile</span>
                </div>
                <label className="tech-config__switch">
                  <input
                    type="checkbox"
                    name="com1Enabled"
                    checked={formValues.com1Enabled}
                    onChange={updateValue}
                    disabled={isReadOnly}
                  />
                  <span aria-hidden="true" />
                </label>
              </div>

              <div className="tech-config__fields tech-config__fields--rs485">
                <SelectInput label="Baud Rate" name="com1Baud" value={formValues.com1Baud} onChange={updateValue} disabled={isReadOnly}>
                  <option value="">Select baud rate</option>
                  {RS485_BAUD_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </SelectInput>
                <SelectInput label="Data Bit" name="com1DataBit" value={formValues.com1DataBit} onChange={updateValue} disabled={isReadOnly}>
                  <option value="">Select data bit</option>
                  {RS485_DATA_BIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </SelectInput>
                <SelectInput label="Stop Bit" name="com1StopBit" value={formValues.com1StopBit} onChange={updateValue} disabled={isReadOnly}>
                  <option value="">Select stop bit</option>
                  {RS485_STOP_BIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </SelectInput>
                <SelectInput label="Parity" name="com1Parity" value={formValues.com1Parity} onChange={updateValue} disabled={isReadOnly}>
                  <option value="">Select parity</option>
                  {RS485_PARITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </SelectInput>
                <SelectInput label="Device" name="com1Mode" value={formValues.com1Mode} onChange={updateValue} disabled={isReadOnly}>
                  <option value="">Select device</option>
                  {RS485_MODE_OPTIONS.map((option) => (
                    <option key={getOptionValue(option)} value={getOptionValue(option)}>{getOptionLabel(option)}</option>
                  ))}
                </SelectInput>
                <div className={`tech-config__meter-fields ${isCom1Inverter ? 'tech-config__meter-fields--single' : ''}`}>
                  <div className="tech-config__meter-column">
                    <h3>{isCom1Inverter ? 'Inverter' : 'Meter 1 (Grid)'}</h3>
                    <TextInput label="Address" name="com1Address" value={formValues.com1Address} onChange={updateValue} disabled={isReadOnly} placeholder="1" />
                    <SelectInput label="Model" name="com1Model" value={formValues.com1Model} onChange={updateValue} disabled={isReadOnly}>
                      <option value="">Select model</option>
                      {ensureSelectedOption(modelOptions, formValues.com1Model).map((option) => (
                        <option key={getOptionValue(option)} value={getOptionValue(option)}>{getOptionLabel(option)}</option>
                      ))}
                    </SelectInput>
                  </div>
                  {!isCom1Inverter ? (
                    <div className="tech-config__meter-column">
                      <h3>Meter 2 (Inverter)</h3>
                      <TextInput label="Address" name="com1Address2" value={formValues.com1Address2} onChange={updateValue} disabled={isReadOnly} placeholder="2" />
                      <SelectInput label="Model" name="com1Model2" value={formValues.com1Model2} onChange={updateValue} disabled={isReadOnly}>
                        <option value="">Select model</option>
                        {ensureSelectedOption(modelOptions, formValues.com1Model2).map((option) => (
                          <option key={getOptionValue(option)} value={getOptionValue(option)}>{getOptionLabel(option)}</option>
                        ))}
                      </SelectInput>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="tech-config__note">
          <LockIcon aria-hidden="true" />
          <span>Configuration fields stay locked until a serial device is connected and Edit Config is selected.</span>
        </div>
      </form>

      {isSerialWarningOpen ? (
        <div
          className="tech-config__warning-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsSerialWarningOpen(false)
            }
          }}
        >
          <section
            className="tech-config__warning-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="serial-warning-title"
            aria-describedby="serial-warning-description"
          >
            <div className="tech-config__warning-icon" aria-hidden="true">
              <WarningAmberIcon />
            </div>
            <div className="tech-config__warning-copy">
              <h2 id="serial-warning-title">No configuration received</h2>
              <p id="serial-warning-description">{SERIAL_RESPONSE_WARNING}</p>
            </div>
            <PrimaryButton type="button" autoFocus onClick={() => setIsSerialWarningOpen(false)}>
              Okay
            </PrimaryButton>
          </section>
        </div>
      ) : null}
    </main>
  )
}

export default TechConfigPage
